import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Info } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { EmptyState, SectionLabel } from "@/components/app/primitives";
import { moneyFull, monthLabel, monthShort, EMPTY_MARK } from "@/lib/format";

const NUM_FIELDS = [
  { key: "cash_in_bank", label: "Cash in bank (S$)" },
  { key: "monthly_burn", label: "Monthly burn (S$)" },
  { key: "revenue", label: "Revenue (S$)" },
  { key: "new_customers", label: "New customers" },
  { key: "active_customers", label: "Active customers" },
  { key: "churn_rate", label: "Churn rate (%)" },
  { key: "ltv_manual", label: "LTV manual (S$, optional)" },
];

export const EditMetricDialog = ({ ventureId, monthsData = [], defaultMonth, trigger, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(defaultMonth || new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({});

  const prefill = (m) => {
    const existing = monthsData.find((x) => x.month === m) || {};
    return Object.fromEntries(NUM_FIELDS.map((f) => [f.key, existing[f.key] ?? ""]));
  };
  const handleOpenChange = (o) => {
    setOpen(o);
    if (o) {
      const m = defaultMonth || new Date().toISOString().slice(0, 7);
      setMonth(m);
      setForm(prefill(m));
    }
  };
  const changeMonth = (m) => { setMonth(m); setForm(prefill(m)); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!month) { toast.error("Please choose a month"); return; }
    setSaving(true);
    try {
      const payload = { month };
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && !isNaN(Number(v))) payload[k] = Number(v);
      });
      await api.post(`/mission-control/ventures/${ventureId}/metrics`, payload);
      toast.success(`Metrics saved for ${monthLabel(month)}`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save metrics");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="edit-metrics-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Enter month values</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Month</Label>
            <Input
              data-testid="admin-metrics-month-select"
              type="month"
              value={month}
              onChange={(e) => changeMonth(e.target.value)}
              className="font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {NUM_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{f.label}</Label>
                <Input
                  data-testid={`admin-metric-input-${f.key}`}
                  type="number"
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="font-mono"
                />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="admin-metrics-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">
            {saving ? "Saving" : "Save month"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TargetDialog = ({ ventureId, target, trigger, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(target ?? 3);

  const handleOpenChange = (o) => { setOpen(o); if (o) setValue(target ?? 3); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/mission-control/ventures/${ventureId}/settings`, { ltv_cac_target: Number(value) });
      toast.success("Benchmark target updated");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update target");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-sm" data-testid="venture-cac-ltv-target-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">LTV:CAC target</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Ideal ratio (a common standard is 3)</Label>
          <Input data-testid="venture-cac-ltv-target-input" type="number" step="0.1" value={value} onChange={(e) => setValue(e.target.value)} className="font-mono" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save target"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function AddCacDialog({ ventureId, categories = [], defaultMonth, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    month: defaultMonth || new Date().toISOString().slice(0, 7),
    description: "", category: categories[0] || "Paid advertising", amount: "", counts_as_cac: true,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.description.trim()) { toast.error("Add a description"); return; }
    setSaving(true);
    try {
      await api.post(`/mission-control/ventures/${ventureId}/cac-expenses`, {
        month: form.month, description: form.description, category: form.category,
        amount: Number(form.amount) || 0, counts_as_cac: form.counts_as_cac,
      });
      toast.success("Expense added");
      setOpen(false);
      setForm((f) => ({ ...f, description: "", amount: "" }));
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add expense");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="venture-cac-expenses-add" size="sm" variant="outline" className="h-8 gap-1.5">
          <Plus className="h-3.5 w-3.5" strokeWidth={1.75} /> Add expense
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="venture-cac-expenses-add-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">Add CAC expense</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Month</Label>
              <Input type="month" value={form.month} onChange={(e) => set("month", e.target.value)} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Amount (S$)</Label>
              <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="font-mono" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Description</Label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border bg-background/40 px-3 py-2">
            <span className="text-sm text-foreground">Counts as CAC</span>
            <Switch checked={form.counts_as_cac} onCheckedChange={(v) => set("counts_as_cac", v)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Add expense"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CacExpensesPanel = ({ ventureId, expenses = [], categories = [], guidance, canEdit, defaultMonth, onChanged }) => {
  const toggle = async (e, value) => {
    try {
      await api.patch(`/mission-control/cac-expenses/${e.id}`, { counts_as_cac: value });
      onChanged && onChanged();
    } catch (err) {
      toast.error("Could not update line item");
    }
  };
  const remove = async (e) => {
    try {
      await api.delete(`/mission-control/cac-expenses/${e.id}`);
      toast.success("Expense removed");
      onChanged && onChanged();
    } catch (err) {
      toast.error("Could not remove expense");
    }
  };
  return (
    <div>
      {guidance && (
        <div className="mb-3 flex gap-2 rounded-md border border-border bg-background/40 px-3 py-2.5">
          <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-xs text-muted-foreground leading-5">{guidance}</p>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <SectionLabel>Line items</SectionLabel>
        {canEdit && <AddCacDialog ventureId={ventureId} categories={categories} defaultMonth={defaultMonth} onSaved={onChanged} />}
      </div>
      {expenses.length === 0 ? (
        <EmptyState message="No CAC line items recorded yet." />
      ) : (
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <Table data-testid="venture-cac-expenses-table">
            <TableHeader>
              <TableRow className="bg-background/40 hover:bg-background/40">
                <TableHead className="text-[11px] uppercase tracking-[0.12em]">Month</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.12em]">Description</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.12em]">Category</TableHead>
                <TableHead className="text-right text-[11px] uppercase tracking-[0.12em]">Amount</TableHead>
                <TableHead className="text-center text-[11px] uppercase tracking-[0.12em]">Counts as CAC</TableHead>
                {canEdit && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((e) => (
                <TableRow key={e.id} data-testid={`venture-cac-expenses-row-${e.id}`} className="hover:bg-white/5">
                  <TableCell className="font-mono text-xs text-muted-foreground">{monthShort(e.month)}</TableCell>
                  <TableCell className="text-xs text-foreground">{e.description}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.category}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs text-foreground">{moneyFull(e.amount)}</TableCell>
                  <TableCell className="text-center">
                    {canEdit ? (
                      <Switch data-testid={`venture-cac-expenses-counts-toggle-${e.id}`} checked={!!e.counts_as_cac} onCheckedChange={(v) => toggle(e, v)} className="scale-90" />
                    ) : (
                      <span className="text-xs text-muted-foreground">{e.counts_as_cac ? "Yes" : "No"}</span>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <button data-testid={`venture-cac-expenses-delete-${e.id}`} onClick={() => remove(e)} className="text-muted-foreground hover:text-[#FFB4B4] transition-colors">
                        <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
